import React, { useEffect, useRef, useState } from 'react';
import { Flex, IconButton, Slider, SliderTrack, SliderFilledTrack, SliderThumb, Text, useColorModeValue, HStack, Box } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause, faVolumeHigh, faVolumeMute } from '@fortawesome/free-solid-svg-icons';
import { S3Client } from '@aws-sdk/client-s3';
import { getSignedS3Url } from './signedImage';

interface QuizAudioPlayerProps {
    s3Client: S3Client | null;
    audioUrl: string;
    title?: string;
    compact?: boolean;
}

export const QuizAudioPlayer: React.FC<QuizAudioPlayerProps> = ({ s3Client, audioUrl, title, compact = false }) => {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [signedUrl, setSignedUrl] = useState<string>('');
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [duration, setDuration] = useState<number>(0);
    const [currentTime, setCurrentTime] = useState<number>(0);
    const [isMuted, setIsMuted] = useState<boolean>(false);

    useEffect(() => {
        let isMounted = true;
        const loadUrl = async () => {
            if (!audioUrl) return;
            const signed = await getSignedS3Url(s3Client, audioUrl);
            if (isMounted) setSignedUrl(signed || audioUrl);
        };
        loadUrl();
        return () => {
            isMounted = false;
        };
    }, [audioUrl, s3Client]);

    const formatTime = (seconds: number) => {
        if (isNaN(seconds) || seconds === 0) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const togglePlay = () => {
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
            setIsPlaying(false);
        } else {
            audioRef.current.play().then(() => {
                setIsPlaying(true);
            }).catch((err) => {
                console.error("Audio playback error:", err);
            });
        }
    };

    const handleSeek = (val: number) => {
        if (!audioRef.current) return;
        audioRef.current.currentTime = val;
        setCurrentTime(val);
    };

    const toggleMute = () => {
        if (!audioRef.current) return;
        audioRef.current.muted = !isMuted;
        setIsMuted(!isMuted);
    };

    const bg = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('teal.200', 'teal.700');

    if (!signedUrl) {
        return (
            <Flex align="center" gap={2} p={2} bg="teal.50" borderRadius="xl">
                <Text fontSize="xs" color="teal.600">Loading audio...</Text>
            </Flex>
        );
    }

    return (
        <Flex
            direction="column"
            w="100%"
            bg={bg}
            p={compact ? 2.5 : 3.5}
            borderRadius="xl"
            border="1px solid"
            borderColor={border}
            shadow="sm"
            gap={2}
        >
            <audio
                ref={audioRef}
                src={signedUrl}
                onLoadedMetadata={(e) => {
                    const target = e.currentTarget;
                    if (target.duration && !isNaN(target.duration)) {
                        setDuration(target.duration);
                    }
                }}
                onTimeUpdate={(e) => {
                    setCurrentTime(e.currentTarget.currentTime);
                }}
                onEnded={() => {
                    setIsPlaying(false);
                    setCurrentTime(0);
                }}
                preload="metadata"
            />

            <Flex align="center" justify="space-between" gap={3}>
                <HStack spacing={2} flex={1} overflow="hidden">
                    <IconButton
                        aria-label={isPlaying ? "Pause" : "Play"}
                        icon={<FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />}
                        size={compact ? "sm" : "md"}
                        colorScheme="teal"
                        borderRadius="full"
                        onClick={togglePlay}
                    />

                    {title && (
                        <Box overflow="hidden">
                            <Text fontSize="xs" fontWeight="bold" noOfLines={1}>
                                {title}
                            </Text>
                        </Box>
                    )}
                </HStack>

                <HStack spacing={2}>
                    <Text fontSize="2xs" fontWeight="bold" color="gray.500" fontFamily="monospace">
                        {formatTime(currentTime)} / {formatTime(duration)}
                    </Text>
                    <IconButton
                        aria-label={isMuted ? "Unmute" : "Mute"}
                        icon={<FontAwesomeIcon icon={isMuted ? faVolumeMute : faVolumeHigh} />}
                        size="xs"
                        variant="ghost"
                        colorScheme="teal"
                        onClick={toggleMute}
                    />
                </HStack>
            </Flex>

            {/* Seek Bar */}
            <Slider
                aria-label="Audio progress slider"
                value={currentTime}
                min={0}
                max={duration || 100}
                onChange={handleSeek}
                focusThumbOnChange={false}
                colorScheme="teal"
                size="sm"
            >
                <SliderTrack bg={useColorModeValue("gray.100", "gray.700")} h="6px" borderRadius="full">
                    <SliderFilledTrack bg="teal.400" />
                </SliderTrack>
                <SliderThumb boxSize={3.5} bg="teal.500" />
            </Slider>
        </Flex>
    );
};

export default QuizAudioPlayer;
